import {
  forceRefreshClerkTokenForApi,
  hasClerkTokenProvider,
  isInvalidOrExpiredClerkTokenResponseData,
} from './clerkTokenProvider';

type FetchImplementation = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/**
 * Retries one authenticated fetch after bypassing Clerk's token cache.
 * A second 401 is returned to the caller so genuine unauthorized sessions keep
 * their existing sign-out behavior.
 */
export async function fetchWithClerkTokenRetry(
  input: RequestInfo | URL,
  init: RequestInit,
  fetchImplementation: FetchImplementation = fetch,
): Promise<Response> {
  const response = await fetchImplementation(input, init);
  if (
    response.status !== 401 ||
    !hasClerkTokenProvider() ||
    !(await response
      .clone()
      .json()
      .then(isInvalidOrExpiredClerkTokenResponseData)
      .catch(() => false))
  ) {
    return response;
  }

  const freshToken = await forceRefreshClerkTokenForApi();
  if (!freshToken) {
    return response;
  }

  const retryHeaders = new Headers(init.headers);
  retryHeaders.set('Authorization', `Bearer ${freshToken}`);

  try {
    await response.body?.cancel();
  } catch {
    // The rejected response is no longer needed; cleanup must not block the authenticated retry.
  }

  return fetchImplementation(input, {
    ...init,
    headers: retryHeaders,
  });
}
